import React from 'react';
import { defineMessages } from 'react-intl';
import useFormatMessage from 'hooks/useFormatMessage';
import Button from '@alltrails/shared/denali/components/Button';

import * as styles from './styles/styles.module.scss';

const DESCRIPTION_STRINGS = defineMessages({
  NO_DESCRIPTION_ADDED: { defaultMessage: 'No description added' },
  TELL_WHAT_YOU_DID: { defaultMessage: 'Tell people what you did' },
  SAVING: { defaultMessage: 'Saving...' },
  CANNOT_SAVE_UPDATE: { defaultMessage: 'Cannot save your update. Please try again.' },
  SAVE: { defaultMessage: 'Save' },
  CANCEL: { defaultMessage: 'Cancel' },
  ADD: { defaultMessage: 'Add' }
});

type Props = {
  canEdit: boolean;
  data: string;
  placeholderText: string;
  isEditShown: boolean;
  submitInProgress: boolean;
  submitFailed: boolean;
  hide: () => void;
  handleSubmit: () => void;
  dataSource: string;
  descriptionValue: string;
  show: () => void;
  handleEditDescription: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
};

const TextEditItem = ({
  canEdit,
  data,
  placeholderText,
  isEditShown,
  submitInProgress,
  submitFailed,
  hide,
  handleSubmit,
  dataSource,
  descriptionValue,
  show,
  handleEditDescription
}: Props): JSX.Element => {
  const {
    formattedDefaultMessages: { NO_DESCRIPTION_ADDED, TELL_WHAT_YOU_DID, SAVING, CANNOT_SAVE_UPDATE, SAVE, CANCEL, ADD }
  } = useFormatMessage(DESCRIPTION_STRINGS);
  let itemText: JSX.Element;
  let newPlaceholderText = NO_DESCRIPTION_ADDED;
  if (placeholderText) {
    newPlaceholderText = placeholderText;
  } else if (canEdit) {
    newPlaceholderText = TELL_WHAT_YOU_DID;
  }

  if (isEditShown) {
    // Edit mode
    let statusText = null;
    let textAreaDisabled = false;
    if (submitInProgress) {
      statusText = SAVING;
      textAreaDisabled = true;
    } else if (submitFailed) {
      statusText = CANNOT_SAVE_UPDATE;
    } else {
      statusText = SAVE;
    }

    const buttonContainer = (
      <div className={styles.editButtons}>
        <Button text={CANCEL} onClick={hide} disabled={submitInProgress} testId="tracks-description-cancel" variant="flat" />
        |
        <Button text={statusText} onClick={handleSubmit} disabled={submitInProgress} testId={statusText} variant="flat" />
      </div>
    );
    const textarea = (
      <textarea
        className={styles.textArea}
        disabled={textAreaDisabled}
        placeholder={newPlaceholderText}
        itemProp="description"
        value={descriptionValue}
        onChange={handleEditDescription}
      />
    );
    itemText = (
      <div className={styles.tabContent}>
        {textarea}
        {buttonContainer}
      </div>
    );
  } else {
    // Non-Edit Mode
    let innerText = placeholderText;
    if (dataSource) {
      innerText = dataSource;
    } else if (data) {
      innerText = data;
    }

    if (canEdit) {
      // User has ability to edit
      const linkText = ADD;
      itemText = (
        <div className={styles.emptyTabContent}>
          <span className={styles.innerText}>{innerText}</span>
          <div className={styles.emptyTabBtn}>
            <Button text={linkText} onClick={show} testId={linkText} variant="flat" />
          </div>
        </div>
      );
    } else {
      // Default
      itemText = (
        <div className={styles.tabContent}>
          <span className={styles.innerText}>{innerText}</span>
        </div>
      );
    }
  }
  return itemText;
};

const DescriptionSection = ({
  placeholderText,
  data,
  canEdit,
  dataSource,
  handleSubmit,
  isEditShown,
  submitInProgress,
  handleEditDescription,
  submitFailed,
  hide,
  show,
  descriptionValue
}: Props): JSX.Element => {
  if ((!data || !data.length) && !canEdit) {
    placeholderText = '';
  }
  return (
    <div className={styles.descriptionSection}>
      <TextEditItem
        data={data}
        dataSource={dataSource}
        canEdit={canEdit}
        placeholderText={placeholderText}
        handleEditDescription={handleEditDescription}
        handleSubmit={handleSubmit}
        isEditShown={isEditShown}
        submitInProgress={submitInProgress}
        submitFailed={submitFailed}
        hide={hide}
        descriptionValue={descriptionValue}
        show={show}
      />
    </div>
  );
};

export default DescriptionSection;
